import { useEffect, useRef, useState } from 'react'
import { formatSeconds, MAX_SECONDS, startRecording, type Recorder } from '@core/voice'
import { Button } from '@design/components'

/**
 * "Zeg er iets bij."
 *
 * Offered after the pages are taken and never before, because the send flow
 * asks the sender no questions (rule 20) and this is an offer, not a step.
 * Skipping it is one tap — the send button is still the biggest thing on the
 * screen.
 *
 * It stops itself at ninety seconds. A note that turns into a monologue is a
 * note nobody plays back.
 */
export function VoiceNote({
  onRecorded,
  recorded,
  onClear,
}: {
  onRecorded: (blob: Blob) => void
  recorded: Blob | null
  onClear: () => void
}) {
  const [recording, setRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState('')
  const recorder = useRef<Recorder | null>(null)

  useEffect(() => {
    if (!recording) return
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(timer)
  }, [recording])

  useEffect(() => {
    if (seconds >= MAX_SECONDS && recording) void stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds, recording])

  // Whatever happens to this screen, the microphone light goes out.
  useEffect(() => () => recorder.current?.cancel(), [])

  async function start() {
    setError('')
    try {
      recorder.current = await startRecording()
      setSeconds(0)
      setRecording(true)
    } catch {
      setError('Ik kan de microfoon niet gebruiken. Sta het toe in de instellingen van je telefoon.')
    }
  }

  async function stop() {
    const active = recorder.current
    recorder.current = null
    setRecording(false)
    if (!active) return
    const blob = await active.stop()
    if (blob.size > 0) onRecorded(blob)
  }

  if (recorded) {
    return (
      <div className="flex items-center gap-3 px-1">
        <span className="flex-1 text-sm text-muted">Gesproken notitie toegevoegd</span>
        <button onClick={onClear} className="text-sm underline underline-offset-2 py-2">
          Wissen
        </button>
      </div>
    )
  }

  if (recording) {
    return (
      <div className="space-y-2">
        <p className="text-center text-sm text-danger tabular-nums" aria-live="polite">
          ● Opnemen — {formatSeconds(seconds)}
        </p>
        <Button size="large" tone="secondary" onClick={stop}>
          Stoppen
        </Button>
      </div>
    )
  }

  return (
    <div>
      {error && <p className="text-sm text-danger mb-2">{error}</p>}
      <Button tone="quiet" onClick={start}>
        Iets erbij zeggen
      </Button>
    </div>
  )
}
